import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
const PhotoTrain = ({ photos, height = {
    xs: 280,
    sm: 360,
    md: 460,
}, }) => {
    const trackRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const dragState = useRef({
        startX: 0,
        scrollLeft: 0,
    });
    /*
     * Store the natural aspect ratio of each image.
     */
    const [aspectRatios, setAspectRatios] = useState({});
    /*
     * Load the images and determine their
     * natural dimensions.
     */
    useEffect(() => {
        photos.forEach((photo) => {
            if (aspectRatios[photo.src])
                return;
            const image = new Image();
            image.onload = () => {
                if (image.naturalHeight === 0)
                    return;
                setAspectRatios((previous) => ({
                    ...previous,
                    [photo.src]: image.naturalWidth / image.naturalHeight,
                }));
            };
            image.src = photo.src;
        });
    }, [photos, aspectRatios]);
    /*
     * Three copies allow us to create an
     * effectively endless train.
     */
    const repeatedPhotos = [...photos, ...photos, ...photos];
    /*
     * Start in the middle copy.
     */
    useEffect(() => {
        const track = trackRef.current;
        if (!track || !photos?.length)
            return;
        const images = Array.from(track.querySelectorAll("img"));
        const positionTrack = () => {
            track.scrollLeft = track.scrollWidth / 3;
        };
        const unloadedImages = images.filter((img) => !img.complete);
        if (unloadedImages.length === 0) {
            positionTrack();
            return;
        }
        let loaded = 0;
        const handleLoad = () => {
            loaded += 1;
            if (loaded === unloadedImages.length) {
                positionTrack();
            }
        };
        unloadedImages.forEach((img) => {
            img.addEventListener("load", handleLoad);
        });
        return () => {
            unloadedImages.forEach((img) => {
                img.removeEventListener("load", handleLoad);
            });
        };
    }, [photos]);
    /*
     * Click-and-drag is a MOUSE-only affordance. Touch gets native scrolling
     * instead (see touchAction below) — trying to reimplement drag-to-scroll
     * for touch ourselves would mean reinventing momentum/inertia the browser
     * already gives us for free, and was the actual source of "doesn't work
     * on mobile at all": touchAction: "pan-y" disabled native horizontal
     * touch scrolling, while these handlers only handled mouse input well,
     * leaving touch with no working scroll mechanism at all.
     */
    const handlePointerDown = (event) => {
        if (event.pointerType !== "mouse")
            return;
        if (!trackRef.current)
            return;
        setIsDragging(true);
        dragState.current = {
            startX: event.clientX,
            scrollLeft: trackRef.current.scrollLeft,
        };
        trackRef.current.setPointerCapture(event.pointerId);
    };
    const handlePointerMove = (event) => {
        if (event.pointerType !== "mouse")
            return;
        if (!isDragging || !trackRef.current) {
            return;
        }
        const distance = event.clientX - dragState.current.startX;
        trackRef.current.scrollLeft = dragState.current.scrollLeft - distance;
    };
    const stopDragging = () => {
        setIsDragging(false);
    };
    /*
     * Keep the user inside the middle copy. Fires on any scroll regardless
     * of source (native touch scroll, mouse drag, or programmatic), so this
     * needed no changes.
     */
    const handleScroll = () => {
        const track = trackRef.current;
        if (!track)
            return;
        const oneSetWidth = track.scrollWidth / 3;
        if (track.scrollLeft < oneSetWidth * 0.25) {
            track.scrollLeft += oneSetWidth;
        }
        if (track.scrollLeft > oneSetWidth * 1.75) {
            track.scrollLeft -= oneSetWidth;
        }
    };
    return (React.createElement(Box, { sx: {
            width: "100%",
            overflow: "hidden",
            position: "relative",
        } },
        React.createElement(Box, { ref: trackRef, onScroll: handleScroll, onPointerDown: handlePointerDown, onPointerMove: handlePointerMove, onPointerUp: stopDragging, onPointerCancel: stopDragging, sx: {
                display: "flex",
                gap: {
                    xs: 1,
                    sm: 1.5,
                    md: 2,
                },
                height,
                overflowX: "auto",
                overflowY: "hidden",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": {
                    display: "none",
                },
                /*
                 * "auto" (the default) lets the browser handle horizontal touch
                 * scrolling natively — including momentum/inertia — while
                 * vertical swipes still pass through to scroll the page, since
                 * this element has nothing to scroll vertically (overflowY is
                 * hidden). Previously this was "pan-y", which explicitly
                 * disabled native horizontal touch scrolling and left touch
                 * users with no working scroll mechanism, since the drag
                 * handlers below are mouse-only.
                 */
                touchAction: "auto",
                // Smoother momentum scrolling on older iOS Safari versions.
                WebkitOverflowScrolling: "touch",
                cursor: isDragging ? "grabbing" : "grab",
                userSelect: "none",
                /*
                 * Reveal a little more of the next
                 * photograph.
                 */
                pl: {
                    xs: "6vw",
                    md: "8vw",
                },
                pr: {
                    xs: "12vw",
                    md: "15vw",
                },
                overscrollBehaviorX: "contain",
            } }, repeatedPhotos.map((photo, index) => {
            const aspectRatio = aspectRatios[photo.src];
            return (React.createElement(Box, { key: `${photo.src}-${index}`, component: "img", src: photo.src, alt: photo.alt || "", draggable: false, loading: "lazy", sx: {
                    height: "100%",
                    width: "auto",
                    maxWidth: "none",
                    flexShrink: 0,
                    display: "block",
                    objectFit: "contain",
                    pointerEvents: "none",
                } }));
        })),
        React.createElement(Box, { sx: {
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: {
                    xs: "5vw",
                    md: "7vw",
                },
                pointerEvents: "none",
                background: "linear-gradient(to right, rgba(250,246,239,0.65), transparent)",
            } }),
        React.createElement(Box, { sx: {
                position: "absolute",
                right: 0,
                top: 0,
                bottom: 0,
                width: {
                    xs: "8vw",
                    md: "12vw",
                },
                pointerEvents: "none",
                background: "linear-gradient(to left, rgba(250,246,239,0.75), transparent)",
            } })));
};
export default PhotoTrain;
